 
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Button, Dialog, DialogContent, DialogActions,
  Grid, Popover, Typography, TextField, Chip, Tooltip, CircularProgress,
  alpha, InputAdornment, useMediaQuery, useTheme, Stack, Checkbox,
  FormControlLabel, Collapse, Snackbar, Alert, Autocomplete,
} from "@mui/material";
import {
  Edit as EditIcon, Delete as DeleteIcon, Print as PrintIcon,
  FilterList as FilterListIcon, Close as CloseIcon, Warning as WarningIcon,
  Refresh as RefreshIcon, Download as DownloadIcon, Business as BusinessIcon,
  Receipt as ReceiptIcon, LocalShipping as TruckIcon, DateRange as DateIcon,
  Visibility, VisibilityOff, CheckCircle, Phone, LocationOn,
} from "@mui/icons-material";
import {
  freightList, freightComplete, freightDelete, updateFreight, freightPrint,
} from "../../../Services/Services";
import { branchpartyList } from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";

// ─── Constants ───────────────────────────────────────────────────
import { brandColor as brand } from "../../../theme";
import { T } from "../../../theme";
const brandBg = alpha(brand, 0.05);

// ─── Shared style objects ─────────────────────────────────────────
const noRadius = { borderRadius: "0px !important", border: "1px solid #e0e0e0dd"  };
const noBorder = { border: "1px solid #e0e0e0dd" };
const noShadow = { boxShadow: "none" };
const noBorderB = { borderBottom: "0.8px solid #a9a9a9" };

const stickyBlur = {
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
};

const textFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    ...noRadius,
    bgcolor: alpha(brand, 0.05),
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: brand },
};

const disabledFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    ...noRadius,
    bgcolor: alpha("#9ca3af", 0.1),
    "& fieldset": { border: "none" },
    "&.Mui-disabled fieldset": { border: "none" },
  },
};

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
      })
    : "-";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", minimumFractionDigits: 0,
  }).format(amount || 0);

// ─── ActionButton ─────────────────────────────────────────────────
const ActionButton = ({ tip, icon, color, onClick }) => (
  <Tooltip title={tip} arrow>
    <IconButton
      size="small"
      onClick={onClick}
      sx={{
        width: 28, height: 28,
        ...noRadius,
        bgcolor: alpha(color, 0.08), color,
        "&:hover": { bgcolor: alpha(color, 0.16) },
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 16 } })}
    </IconButton>
  </Tooltip>
);

// ─── MobileFreightCard (Accordion) ───────────────────────────────
const MobileFreightCard = ({ row, idx, expanded, onToggle, onEdit, onDelete, onPrint, onSettle }) => {
  const createdDate = new Date(row.created_at);
  const today = new Date();
  const diffInDays = (today - createdDate) / (1000 * 3600 * 24);
  const isOlderThan10Days = diffInDays > 10;
  const isUnpaid = !row.paid_amount || Number(row.paid_amount) === 0;
  const isOverdue = isOlderThan10Days && isUnpaid;

  const accentColor = row.is_completed
    ? T.success
    : isOverdue
    ? T.red
    : row.is_remember
    ? T.warning
    : brand;

  return (
    <Paper
      sx={{
        mb: 1,
        ...noRadius,
        ...noBorder,
        ...noShadow,
        bgcolor: brandBg,
        overflow: "hidden",
      }}
    >
      {/* ── Top Row ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          minHeight: 75,
          borderLeft: expanded
            ? "4px solid orange"
            : `4px solid ${accentColor}`,
          transition: "border-color 0.2s",
        }}
      >
        {/* Big Icon Left */}
        <Box
          sx={{
            width: 70,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(brand, 0.1),
            borderRight: `1px solid ${alpha(brand, 0.15)}`,
          }}
        >
          <WarningIcon sx={{ fontSize: 26, color: accentColor }} />
          <Typography
            sx={{
              fontSize: "0.55rem",
              fontWeight: 600,
              color: brand,
              mt: 0.5,
            }}
          >
            #{idx + 1}
          </Typography>
          {isOverdue && (
            <Typography
              sx={{
                fontSize: "0.5rem",
                fontWeight: 700,
                color: T.red,
                mt: 0.25,
              }}
            >
              {Math.floor(diffInDays)}d
            </Typography>
          )}
        </Box>

        {/* Middle */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: 1.5,
            py: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.85rem",
              color: brand,
              lineHeight: 1.2,
              mb: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.truck_no || "-"}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.65rem",
              color: "#374151",
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mb: 0.25,
            }}
          >
            {row.party_name || "-"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <LocationOn sx={{ fontSize: 10, color: "#6b7280" }} />
            <Typography
              sx={{
                fontSize: "0.6rem",
                color: "#6b7280",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.from_location || "-"} → {row.to_location || "-"}
            </Typography>
          </Box>

          {/* Status chip */}
          <Box sx={{ mt: 0.5 }}>
            {row.is_completed ? (
              <Chip
                label="Completed"
                size="small"
                icon={<CheckCircle sx={{ fontSize: 10 }} />}
                sx={{
                  ...noRadius,
                  bgcolor: alpha(T.success, 0.1),
                  color: T.success,
                  fontSize: "0.55rem",
                  height: 16,
                  "& .MuiChip-icon": { color: T.success, ml: 0.5 },
                }}
              />
            ) : isOverdue ? (
              <Chip
                label="Overdue"
                size="small"
                icon={<WarningIcon sx={{ fontSize: 10 }} />}
                sx={{
                  ...noRadius,
                  bgcolor: alpha(T.red, 0.1),
                  color: T.red,
                  fontSize: "0.55rem",
                  height: 16,
                  "& .MuiChip-icon": { color: T.red, ml: 0.5 },
                }}
              />
            ) : (
              <Chip
                label="Pending"
                size="small"
                sx={{
                  ...noRadius,
                  bgcolor: alpha(T.warning, 0.1),
                  color: T.warning,
                  fontSize: "0.55rem",
                  height: 16,
                }}
              />
            )}
          </Box>
        </Box>

        {/* Right */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-end",
            px: 1.2,
            py: 1,
            minWidth: 110,
          }}
        >
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: "0.55rem", color: "#6b7280" }}>
              Net Payable
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", color: brand }}>
              ₹{row.net_payable || 0}
            </Typography>
          </Box>

          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 14 }} />
              ) : (
                <Visibility sx={{ fontSize: 14 }} />
              )
            }
            sx={{
              ...noRadius,
              border: `1px solid ${alpha(brand, 0.4)}`,
              color: brand,
              fontSize: "0.6rem",
              textTransform: "none",
              px: 1,
              py: 0.25,
              minHeight: 24,
              bgcolor: "#fff",
              "& .MuiButton-startIcon": {
                marginRight: "4px",
                marginLeft: 0,
              },
              "&:hover": { bgcolor: alpha(brand, 0.08) },
            }}
          >
            {expanded ? "Hide" : "View"}
          </Button>
        </Box>
      </Box>

      {/* ── Expanded Section ── */}
      {expanded && (
        <Box
          sx={{
            bgcolor: "#fff",
            borderTop: `1px solid ${alpha(brand, 0.15)}`,
            p: 1.5,
            borderLeft: "4px solid orange",
          }}
        >
          <Stack spacing={1}>
            {/* Basic Info */}
            <Box>
              <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: brand, mb: 0.5 }}>
                📋 Basic Info
              </Typography>
              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>ID:</strong> {row.freight_id}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>DO:</strong> {row.do_no || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>TP:</strong> {row.tp_no || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>Loading:</strong> {formatDate(row.loading_date)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>Unloading:</strong> {formatDate(row.unloading_date)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Weight */}
            <Box sx={{ p: 0.75, bgcolor: alpha(brand, 0.04) }}>
              <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: brand, mb: 0.5 }}>
                ⚖️ Weight
              </Typography>
              <Grid container spacing={0.5}>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>Load</Typography>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>{row.loading_wt || 0}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>Recv</Typography>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>{row.received_wt || 0}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>Short</Typography>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: T.red }}>
                    {row.shortage_wt || 0}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>Bill</Typography>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>{row.billing_wt || 0}</Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Charges */}
            <Box>
              <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: brand, mb: 0.5 }}>
                💰 Charges
              </Typography>
              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>Rate:</strong> ₹{row.freight_rate || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>LR:</strong> ₹{row.freight_amt || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>TDS:</strong> ₹{row.tds_amt || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>Shortage:</strong> ₹{row.shortage_amt || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>Commission:</strong> ₹{row.commission || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>Other:</strong> ₹{row.other_exp || 0}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Advance */}
            <Box sx={{ p: 0.75, bgcolor: alpha(T.warning, 0.06) }}>
              <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: T.warning, mb: 0.5 }}>
                💵 Advance
              </Typography>
              <Grid container spacing={0.5}>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>Mines</Typography>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>₹{row.ad_mines_exp || 0}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>Diesel</Typography>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>₹{row.ad_diesel_exp || 0}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>Cash</Typography>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>₹{row.ad_cash_exp || 0}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: T.warning, mt: 0.25 }}>
                    Total: ₹{row.total_advance || 0}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Owner */}
            {(row.truck_owner || row.phone_no || row.pan_no) && (
              <Box>
                <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: brand, mb: 0.5 }}>
                  👤 Owner
                </Typography>
                {row.truck_owner && (
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>Owner:</strong> {row.truck_owner}
                  </Typography>
                )}
                {row.phone_no && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Phone sx={{ fontSize: 11, color: brand }} />
                    <Typography sx={{ fontSize: "0.65rem" }}>{row.phone_no}</Typography>
                  </Box>
                )}
                {row.pan_no && (
                  <Typography sx={{ fontSize: "0.65rem" }}>
                    <strong>PAN:</strong> {row.pan_no}
                  </Typography>
                )}
              </Box>
            )}

            {/* Summary */}
            <Box sx={{ p: 0.75, bgcolor: brandBg, borderLeft: `3px solid ${brand}` }}>
              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>Net Amount</Typography>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>₹{row.net_amount || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>Net Payable</Typography>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: brand }}>
                    ₹{row.net_payable || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>Paid</Typography>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: T.success }}>
                    ₹{row.paid_amount?.toFixed(1) || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>Balance</Typography>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: T.red }}>
                    ₹{row.bal_amount?.toFixed(1) || 0}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {row.remark && (
              <Box>
                <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#6b7280" }}>
                  Remark:
                </Typography>
                <Typography sx={{ fontSize: "0.65rem", color: "#374151" }}>{row.remark}</Typography>
              </Box>
            )}
          </Stack>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mt: 1.5,
              pt: 1.5,
              borderTop: `1px dashed ${alpha(brand, 0.2)}`,
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            {!row.is_completed && (
              <Button
                size="small"
                startIcon={<CheckCircle sx={{ fontSize: 14 }} />}
                onClick={(e) => onSettle(e, row.freight_id)}
                sx={{
                  ...noRadius,
                  bgcolor: alpha(T.success, 0.08),
                  color: T.success,
                  fontSize: "0.65rem",
                  textTransform: "none",
                  px: 1,
                  "&:hover": { bgcolor: alpha(T.success, 0.16) },
                }}
              >
                Settle
              </Button>
            )}
            <Button
              size="small"
              startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
              onClick={() => onPrint(row)}
              sx={{
                ...noRadius,
                bgcolor: alpha("#8b5cf6", 0.08),
                color: "#8b5cf6",
                fontSize: "0.65rem",
                textTransform: "none",
                px: 1,
                "&:hover": { bgcolor: alpha("#8b5cf6", 0.16) },
              }}
            >
              Print
            </Button>
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 14 }} />}
              onClick={() => onEdit(row)}
              sx={{
                ...noRadius,
                bgcolor: alpha(brand, 0.08),
                color: brand,
                fontSize: "0.65rem",
                textTransform: "none",
                px: 1,
                "&:hover": { bgcolor: alpha(brand, 0.16) },
              }}
            >
              Edit
            </Button>
            <Button
              size="small"
              startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => onDelete(e, row.freight_id)}
              sx={{
                ...noRadius,
                bgcolor: alpha("#ef4444", 0.08),
                color: "#ef4444",
                fontSize: "0.65rem",
                textTransform: "none",
                px: 1,
                "&:hover": { bgcolor: alpha("#ef4444", 0.16) },
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// ─── Main Component ───────────────────────────────────────────────
export default function NotRecFreight() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showFilters, setShowFilters] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [data, setData] = useState([]);
  const [partyFilter, setPartyFilter] = useState("");
  const [tpFilter, setTpFilter] = useState("");
  const [loadingDateFilter, setLoadingDateFilter] = useState(null);
  const [fromFilter, setFromFilter] = useState("");
  const [toBillingFilter, setToBillingFilter] = useState("");
  const [truckFilter, setTruckFilter] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState({});
  const [settleAnchorEl, setSettleAnchorEl] = useState(null);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [settleId, setSettleId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [partyOptions, setParties] = useState([]);
  const [excelModalOpen, setExcelModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  // Accordion state
  const [expandedCardId, setExpandedCardId] = useState(null);
  const handleCardToggle = (id) => {
    setExpandedCardId((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    const fetchParties = async () => {
      const data = await branchpartyList();
      if (data) setParties(data.party);
    };
    fetchParties();
  }, []);

  const fetchMoreParties = useCallback(
    async (reset = false, filters = {}) => {
      setLoading(true);
      try {
        const combinedFilters = { ...filters, freight_not_rec: "true" };
        const dataRes = await freightList(reset ? 1 : page, combinedFilters);
        if (dataRes?.results?.length) {
          setData((prev) => (reset ? dataRes.results : [...prev, ...dataRes.results]));
          setPage((prev) => (reset ? 2 : prev + 1));
          setHasMore(!!dataRes.next);
        } else {
          if (reset) setData([]);
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching freight list:", error);
      } finally {
        setLoading(false);
      }
    },
    [page]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchMoreParties(true, {
        party_name: partyFilter,
        do_no: tpFilter,
        ref_no: toBillingFilter,
        truck_no: truckFilter,
        loading_date: loadingDateFilter,
        from_to: fromFilter,
      });
      setExpandedCardId(null);
    }, 500);
    return () => clearTimeout(handler);
  }, [partyFilter, tpFilter, toBillingFilter, truckFilter, loadingDateFilter, fromFilter, fetchMoreParties]);

  const handleComplete = async (id) => {
    await freightComplete(id);
    setData((prev) => prev.filter((row) => row.freight_id !== id));
    setSnackbar({ open: true, message: "LR completed successfully", severity: "success" });
    setSettleAnchorEl(null);
    setSettleId(null);
  };

  const handleDelete = async (id) => {
    await freightDelete(id);
    setData((prev) => prev.filter((row) => row.freight_id !== id));
    setSnackbar({ open: true, message: "Record deleted successfully", severity: "success" });
    setDeleteAnchorEl(null);
    setDeleteId(null);
  };

  const handleEditOpen = (row) => {
    setEditRow({ ...row });
    setEditDialogOpen(true);
  };

  const handleEditChange = (field, value) => {
    const updatedData = { ...editRow, [field]: value };
    const loading = parseFloat(field === "loading_wt" ? value : updatedData.loading_wt) || 0;
    const receiving = parseFloat(field === "received_wt" ? value : updatedData.received_wt) || 0;
    const freightRate = parseFloat(field === "freight_rate" ? value : updatedData.freight_rate) || 0;
    const tdsRate = parseFloat(field === "tds_per" ? value : updatedData.tds_per) || 0;
    const shortageRate = parseFloat(field === "shortage_rate" ? value : updatedData.shortage_rate) || 0;
    const commission = parseFloat(field === "commission" ? value : updatedData.commission) || 0;
    const minesAdvance = parseFloat(field === "ad_mines_exp" ? value : updatedData.ad_mines_exp) || 0;
    const dieselAdvance = parseFloat(field === "ad_diesel_exp" ? value : updatedData.ad_diesel_exp) || 0;
    const cashAdvance = parseFloat(field === "ad_cash_exp" ? value : updatedData.ad_cash_exp) || 0;
    const otherExp = parseFloat(field === "other_exp" ? value : updatedData.other_exp) || 0;

    updatedData.billing_wt = Math.min(loading, receiving).toFixed(2);
    const shortage = (loading - receiving) * 1000;
    updatedData.shortage_wt = shortage > 0 ? shortage.toFixed(2) : "0";
    const billing = parseFloat(updatedData.billing_wt) || 0;
    updatedData.freight_amt = (freightRate * billing).toFixed(2);
    updatedData.tds_amt = ((parseFloat(updatedData.freight_amt) * tdsRate) / 100).toFixed(2);
    updatedData.shortage_amt = (parseFloat(updatedData.shortage_wt) * shortageRate).toFixed(2);
    updatedData.total_advance = (minesAdvance + dieselAdvance + cashAdvance).toFixed(2);
    updatedData.net_amount = (
      parseFloat(updatedData.freight_amt) -
      parseFloat(updatedData.tds_amt) -
      parseFloat(updatedData.shortage_amt) -
      parseFloat(updatedData.total_advance) -
      commission
    ).toFixed(2);
    updatedData.net_payable = (parseFloat(updatedData.net_amount) - otherExp).toFixed(2);
    setEditRow(updatedData);
  };

  const handleEditSave = async () => {
    const saveData = { ...editRow };
    saveData.receive_amount = saveData.receive_amount || 0;
    saveData.bal_amount = (
      parseFloat(saveData.net_payable || 0) - parseFloat(saveData.paid_amount || 0)
    ).toFixed(2);
    const res_data = await updateFreight(saveData.freight_id, saveData);
    setData((prev) => prev.map((row) => (row.freight_id === res_data.freight_id ? res_data : row)));
    setEditDialogOpen(false);
    setSnackbar({ open: true, message: "Record updated successfully", severity: "success" });
  };

  const handlePrintSingleRow = async (row) => {
    const printData = await freightPrint(row.freight_id);
    const printWindow = window.open("", "", "width=900,height=600");
    const fieldPairs = [
      ["loading_date", "unloading_date"], ["from_location", "to_location"],
      ["do_no", "tp_no"], ["loading_wt", "received_wt"],
      ["shortage_wt", "billing_wt"], ["freight_rate", "freight_amt"],
      ["tds_per", "tds_amt"], ["total_advance", "commission"],
      ["shortage_rate", "shortage_amt"], ["net_amount", "other_exp"],
      ["net_payable", "truck_no"],
    ];
    row.payments = printData[0]?.payments || [];
    row.freights = printData[0]?.freights || [];
    const tableRows = fieldPairs
      .map(([key1, key2]) => {
        const value1 = row[key1] ?? "";
        const value2 = row[key2] ?? "";
        const formatTitle = (text) =>
          text.replace(/_/g, " ").replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
        return `<tr><td style="border:1px solid #000;padding:6px;"><strong>${key1 ? formatTitle(key1) : ""}</strong></td><td style="border:1px solid #000;padding:6px;">${value1}</td><td style="width:2%;"></td><td style="border:1px solid #000;padding:6px;"><strong>${key2 ? formatTitle(key2) : ""}</strong></td><td style="border:1px solid #000;padding:6px;">${value2}</td></tr>`;
      })
      .join("");
    printWindow.document.write(`
      <html><head><title>Print LR</title>
      <style>body{font-family:Arial;padding:20px;}header{text-align:center;font-size:20px;font-weight:bold;}footer{position:fixed;bottom:10px;width:100%;text-align:center;font-size:12px;}table{border-collapse:collapse;width:100%;}</style></head>
      <body><header>PARAS ROAD LINES</header>
      <table border="1" style="border-collapse:collapse;" cellpadding="5">
        <tr><td><strong>Ashutosh Ku. Singh</strong></td><td><strong>LR ID: ${row.freight_id}</strong></td><td>${row.truck_owner || ""}</td></tr>
        <tr><td><strong>LR No: ${row.lr_number}</strong></td><td><strong>Net Payable: ${row.net_payable}</strong></td><td>${row.phone_no || ""}</td></tr>
       </table><br/>
      <table border="1" style="border-collapse:collapse;">${tableRows}</table>
      <footer>Powered by: YASHVITECH IT SOLUTION PVT LTD</footer>
      <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();}};</script>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleExportSelectedToExcel = async () => {
    const XLSX = await import("xlsx-js-style");
    const selectedData = data.filter((row) => selectedRows.includes(row.freight_id));
    const exportData = selectedData.map((row, i) => ({
      "S.No": i + 1, "Party Name": row.party_name, "LR No": row.lr_number,
      "Loading Date": row.loading_date, "Unloading Date": row.unloading_date, "Truck No.": row.truck_no,
      "D.O. No.": row.do_no, "T.P. No.": row.tp_no, From: row.from_location, To: row.to_location,
      "Loading Wt": row.loading_wt, "Receiving Wt": row.received_wt, "Shortage Wt": row.shortage_wt,
      "Billing Wt": row.billing_wt, "LR Rate": row.freight_rate, "LR Amt": row.freight_amt,
      "TDS %": row.tds_per, "TDS Amt": row.tds_amt, "Shortage Rate": row.shortage_rate,
      "Shortage Amt": row.shortage_amt, "Ad Mines": row.ad_mines_exp, "Ad Diesel": row.ad_diesel_exp,
      "Ad Cash": row.ad_cash_exp, "Total Ad": row.total_advance, Commission: row.commission,
      "Net Amt": row.net_amount, "Others Exp.": row.other_exp, "Net Payable": row.net_payable,
      "Paid Amt": row.paid_amount, "Bal Amt": row.bal_amount, "Truck Owner": row.truck_owner,
      "Phone No.": row.phone_no, "PAN No.": row.pan_no, Remark: row.remark, Date: formatDate(row.created_at),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Not Received LR");
    XLSX.writeFile(wb, `Not_Received_Freight_${new Date().toISOString().split("T")[0]}.xlsx`);
    setExcelModalOpen(false);
    setSelectedRows([]);
  };

  const filteredData = data.filter((item) => {
    const normalize = (str) => (str ?? "").toLowerCase().trim();
    const smartInput = normalize(fromFilter);
    const [fromPart, toPart] = smartInput.split("-").map((s) => s.trim());
    const fromMatches = fromPart ? normalize(item.from_location).includes(fromPart) : true;
    const toMatches = toPart ? normalize(item.to_location).includes(toPart) : true;
    const looseMatch =
      !toPart &&
      (normalize(item.from_location).includes(smartInput) || normalize(item.to_location).includes(smartInput));
    return (
      (partyFilter === "" || normalize(item.party_name).includes(normalize(partyFilter))) &&
      (tpFilter === "" || (item.do_no?.toString() || "").includes(tpFilter)) &&
      (toBillingFilter === "" || (item.billing?.billing_id?.toString() || "").includes(toBillingFilter)) &&
      (truckFilter === "" || normalize(item.truck_no).includes(normalize(truckFilter))) &&
      (loadingDateFilter === null ||
        item.loading_date === loadingDateFilter.toISOString().split("T")[0]) &&
      ((fromPart && toPart && fromMatches && toMatches) || (!toPart && looseMatch))
    );
  });

  const totals = {
    loading_wt: filteredData.reduce((s, c) => s + (+c.loading_wt || 0), 0).toFixed(3),
    received_wt: filteredData.reduce((s, c) => s + (+c.received_wt || 0), 0).toFixed(3),
    shortage_wt: filteredData.reduce((s, c) => s + (+c.shortage_wt || 0), 0).toFixed(3),
    billing_wt: filteredData.reduce((s, c) => s + (+c.billing_wt || 0), 0).toFixed(3),
    freight_amt: filteredData.reduce((s, c) => s + (+c.freight_amt || 0), 0).toFixed(1),
    tds_amt: filteredData.reduce((s, c) => s + (+c.tds_amt || 0), 0).toFixed(1),
    shortage_amt: filteredData.reduce((s, c) => s + (+c.shortage_amt || 0), 0).toFixed(1),
    total_advance: filteredData.reduce((s, c) => s + (+c.total_advance || 0), 0).toFixed(1),
    commission: filteredData.reduce((s, c) => s + (+c.commission || 0), 0).toFixed(1),
    net_amount: filteredData.reduce((s, c) => s + (+c.net_amount || 0), 0).toFixed(1),
    other_exp: filteredData.reduce((s, c) => s + (+c.other_exp || 0), 0).toFixed(1),
    net_payable: filteredData.reduce((s, c) => s + (+c.net_payable || 0), 0).toFixed(1),
    paid_amount: filteredData.reduce((s, c) => s + (+c.paid_amount || 0), 0).toFixed(1),
    bal_amount: filteredData.reduce((s, c) => s + (+c.bal_amount || 0), 0).toFixed(1),
  };

  // Sticky cell helpers
  const stickyHeaderCell = (left, extraSx = {}) => ({
    fontWeight: "bold",
    fontSize: "0.65rem",
    whiteSpace: "nowrap",
    position: "sticky",
    left,
    zIndex: 11,
    bgcolor: alpha("#f5f5f5", 0.85),
    ...stickyBlur,
    ...noBorderB,
    ...extraSx,
  });

  const stickyBodyCell = (left, rowBg, extraSx = {}) => ({
    position: "sticky",
    left,
    zIndex: 5,
    bgcolor: alpha(rowBg, 0.85),
    ...stickyBlur,
    ...extraSx,
  });

  return (
    <Box sx={{ px: 0, pt: 0, mt: -3, maxWidth: 1400, mx: "auto", width: "100%" }}>

      {/* ── Header ── */}
      <Paper sx={{ ...noRadius, ...noBorder, ...noShadow, overflow: "hidden", mb: 1 }}>
        <Box sx={{ bgcolor: brand, px: 1, py: 1 , bgcolor: alpha("#a42b2b", 0.05)}}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <WarningIcon sx={{ color: "#374151", fontSize: 22 }} />
              <Typography sx={{ fontWeight: 700, color: "#374151", fontSize: "0.9rem" }}>
                Not Received LR
              </Typography>
              <Chip
                label={`${filteredData.length} Pending`}
                size="small"
                sx={{
                  bgcolor: alpha("#374151", 0.2),
                  color: "#374151",
                  ...noRadius,
                  fontSize: "0.65rem",
                }}
              />
            </Stack>
            <Tooltip title="Export to Excel">
              <Button
                size="small"
                onClick={() => setExcelModalOpen(true)}
                startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                sx={{
                  ...noRadius,
                  border: "1px solid #374151",
                  color: "#374151",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": { bgcolor: alpha("#374151", 0.1), boxShadow: "none" },
                }}
              >
                Export
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* ── Filters ── */}
      <Paper sx={{ ...noRadius, ...noBorder, ...noShadow, overflow: "hidden", mb: 1, p: 1.5 }}>
        {isMobile && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Filters</Typography>
            <IconButton size="small" onClick={() => setShowFilters(!showFilters)} sx={{ color: brand }}>
              {showFilters ? <CloseIcon sx={{ fontSize: 18 }} /> : <FilterListIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Box>
        )}
        <Collapse in={!isMobile || showFilters}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center" flexWrap="wrap">
            <Autocomplete
              options={partyOptions}
              getOptionLabel={(option) => option?.name || ""}
              value={partyOptions.find((opt) => opt.name === partyFilter) || null}
              onChange={(event, newValue) => setPartyFilter(newValue?.name || "")}
              sx={{ flex: 2, width: "100%" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Party Name"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon sx={{ fontSize: 18, color: "#999" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={textFieldSx}
                />
              )}
            />

            <TextField
              fullWidth size="small" placeholder="DO No."
              value={tpFilter} onChange={(e) => setTpFilter(e.target.value)}
              sx={{ ...textFieldSx, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ReceiptIcon sx={{ fontSize: 18, color: "#999" }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth size="small" placeholder="Truck No."
              value={truckFilter} onChange={(e) => setTruckFilter(e.target.value)}
              sx={{ ...textFieldSx, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <TruckIcon sx={{ fontSize: 18, color: "#999" }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth type="date" size="small"
              InputLabelProps={{ shrink: true }}
              value={loadingDateFilter ? loadingDateFilter.toISOString().split("T")[0] : ""}
              onChange={(e) => setLoadingDateFilter(e.target.value ? new Date(e.target.value) : null)}
              sx={{ ...textFieldSx, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateIcon sx={{ fontSize: 18, color: "#999" }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth size="small" placeholder="From - To"
              value={fromFilter} onChange={(e) => setFromFilter(e.target.value)}
              sx={{ ...textFieldSx, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <TruckIcon sx={{ fontSize: 18, color: "#999" }} />
                  </InputAdornment>
                ),
              }}
            />

            <Tooltip title="Clear Filters">
              <IconButton
                size="small"
                onClick={() => {
                  setPartyFilter(""); setTpFilter(""); setTruckFilter("");
                  setLoadingDateFilter(null); setFromFilter(""); setToBillingFilter("");
                }}
                sx={{ bgcolor: alpha(T.red, 0.08), color: T.red, ...noRadius, width: 36, height: 36 }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={() => fetchMoreParties(true)}
                sx={{ bgcolor: alpha(brand, 0.08), color: brand, ...noRadius, width: 36, height: 36 }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Collapse>
      </Paper>

      {/* ── Table / Mobile Card View ── */}
      {isMobile ? (
        <Box sx={{ bgcolor: "transparent" }}>
          {loading && data.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: brand }} />
            </Box>
          ) : filteredData.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <WarningIcon sx={{ fontSize: 48, color: alpha(brand, 0.3), mb: 1 }} />
              <Typography sx={{ fontWeight: 600, color: "#666", fontSize: "0.8rem" }}>
                No not-received freight records
              </Typography>
            </Box>
          ) : (
            <Box
              id="notrec-freight-mobile"
              sx={{ maxHeight: "calc(100vh - 220px)", overflow: "auto" }}
            >
              <InfiniteScroll
                dataLength={data.length}
                next={() => fetchMoreParties(false)}
                hasMore={hasMore}
                loader={
                  <Box sx={{ textAlign: "center", py: 2 }}>
                    <CircularProgress size={20} sx={{ color: brand }} />
                  </Box>
                }
                scrollableTarget="notrec-freight-mobile"
              >
                {filteredData.map((row, idx) => (
                  <MobileFreightCard
                    key={row.freight_id}
                    row={row}
                    idx={idx}
                    expanded={expandedCardId === row.freight_id}
                    onToggle={() => handleCardToggle(row.freight_id)}
                    onEdit={handleEditOpen}
                    onDelete={(e, id) => { setDeleteAnchorEl(e.currentTarget); setDeleteId(id); }}
                    onPrint={handlePrintSingleRow}
                    onSettle={(e, id) => { setSettleAnchorEl(e.currentTarget); setSettleId(id); }}
                  />
                ))}
              </InfiniteScroll>

              {/* Mobile Summary */}
              <Paper
                sx={{
                  ...noRadius, ...noBorder, ...noShadow,
                  bgcolor: brandBg, p: 1.5, mt: 1,
                  borderLeft: `4px solid ${brand}`,
                }}
              >
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: brand, mb: 0.5 }}>
                  📊 Summary
                </Typography>
                <Grid container spacing={0.5}>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>Total LR</Typography>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 700 }}>
                      {formatCurrency(totals.freight_amt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>Net Payable</Typography>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: brand }}>
                      {formatCurrency(totals.net_payable)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>Paid</Typography>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: T.success }}>
                      {formatCurrency(totals.paid_amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: "0.6rem", color: "#6b7280" }}>Balance</Typography>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: T.red }}>
                      {formatCurrency(totals.bal_amount)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
        </Box>
      ) : (
        <Paper sx={{ ...noRadius, ...noBorder, ...noShadow, overflow: "hidden" }}>
          <Box id="notrec-freight-container" sx={{ maxHeight: "calc(100vh - 280px)", overflow: "auto" }}>
            <InfiniteScroll
              dataLength={data.length}
              next={() => fetchMoreParties(false)}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <CircularProgress size={24} sx={{ color: brand }} />
                </Box>
              }
              scrollableTarget="notrec-freight-container"
            >
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 2400,
                    borderCollapse: "collapse",
                    "& td, & th": {
                      py: 0, px: 1,
                      fontSize: "0.65rem",
                      ...noBorderB,
                    },
                    "& th": {
                      fontWeight: 600,
                      color: "#1f2937",
                      bgcolor: alpha(brand, 0.05),
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyHeaderCell(0, { zIndex: 12 })}>Settle</TableCell>
                      <TableCell sx={stickyHeaderCell(40, { zIndex: 12 })}>S.No</TableCell>
                      <TableCell sx={stickyHeaderCell(80, { zIndex: 12 })}>Truck No.</TableCell>
                      <TableCell>LR Id</TableCell>
                      <TableCell>Party Name</TableCell>
                      <TableCell>Loading Date</TableCell>
                      <TableCell>Unloading Date</TableCell>
                      <TableCell>D.O. No.</TableCell>
                      <TableCell>T.P. No.</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell align="right">Loading Wt</TableCell>
                      <TableCell align="right">Receiving Wt</TableCell>
                      <TableCell align="right">Shortage Wt</TableCell>
                      <TableCell align="right">Billing Wt</TableCell>
                      <TableCell align="right">LR Rate</TableCell>
                      <TableCell align="right">LR Amt</TableCell>
                      <TableCell align="right">TDS %</TableCell>
                      <TableCell align="right">TDS Amt</TableCell>
                      <TableCell align="right">Shortage Rate</TableCell>
                      <TableCell align="right">Shortage Amt</TableCell>
                      <TableCell align="right">Ad Mines</TableCell>
                      <TableCell align="right">Ad Diesel</TableCell>
                      <TableCell align="right">Ad Cash</TableCell>
                      <TableCell align="right">Total Ad</TableCell>
                      <TableCell align="right">Commission</TableCell>
                      <TableCell align="right">Net Amt</TableCell>
                      <TableCell align="right">Others Exp.</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: brand }}>Net Payable</TableCell>
                      <TableCell align="right">Paid Amt</TableCell>
                      <TableCell align="right">Bal Amt</TableCell>
                      <TableCell>Truck Owner</TableCell>
                      <TableCell>Phone No.</TableCell>
                      <TableCell>PAN No.</TableCell>
                      <TableCell>Remark</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell sx={stickyHeaderCell(undefined, { right: 0, zIndex: 12 })} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && data.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={37} align="center" sx={{ py: 5, ...noBorderB }}>
                          <CircularProgress sx={{ color: brand }} />
                        </TableCell>
                      </TableRow>
                    ) : filteredData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={37} align="center" sx={{ py: 5, ...noBorderB }}>
                          No records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredData.map((row, idx) => {
                        const createdDate = new Date(row.created_at);
                        const today = new Date();
                        const diffInDays = (today - createdDate) / (1000 * 3600 * 24);
                        const isOlderThan10Days = diffInDays > 10;
                        const isUnpaid = !row.paid_amount || Number(row.paid_amount) === 0;
                        const isOverdue = isOlderThan10Days && isUnpaid;

                        const rowBgColor = row.is_completed
                          ? "#e8f8ee"
                          : isOverdue
                          ? "#fde8e8"
                          : row.is_remember
                          ? "#fff4e6"
                          : idx % 2 === 0
                          ? "#fafafa"
                          : "#fff";

                        return (
                          <TableRow
                            key={row.freight_id}
                            hover
                            sx={{ bgcolor: rowBgColor, "&:hover": { bgcolor: alpha(brand, 0.06) } }}
                          >
                            <TableCell sx={stickyBodyCell(0, rowBgColor)}>
                              <Checkbox
                                size="small"
                                disabled={row.is_completed}
                                onClick={(e) => { setSettleAnchorEl(e.currentTarget); setSettleId(row.freight_id); }}
                                sx={{ color: brand, "&.Mui-checked": { color: brand } }}
                              />
                            </TableCell>
                            <TableCell sx={stickyBodyCell(40, rowBgColor)}>{idx + 1}</TableCell>
                            <TableCell sx={stickyBodyCell(80, rowBgColor)}>
                              <Chip
                                label={row.truck_no}
                                size="small"
                                sx={{ bgcolor: brandBg, color: brand, ...noRadius, fontSize: "0.6rem" }}
                              />
                            </TableCell>
                            <TableCell>{row.freight_id}</TableCell>
                            <TableCell>{row.party_name}</TableCell>
                            <TableCell>{formatDate(row.loading_date)}</TableCell>
                            <TableCell>{formatDate(row.unloading_date)}</TableCell>
                            <TableCell>{row.do_no}</TableCell>
                            <TableCell>{row.tp_no}</TableCell>
                            <TableCell>{row.from_location}</TableCell>
                            <TableCell>{row.to_location}</TableCell>
                            <TableCell align="right">{row.loading_wt}</TableCell>
                            <TableCell align="right">{row.received_wt}</TableCell>
                            <TableCell align="right" sx={{ color: row.shortage_wt > 0 ? T.red : "inherit" }}>
                              {row.shortage_wt}
                            </TableCell>
                            <TableCell align="right">{row.billing_wt}</TableCell>
                            <TableCell align="right">₹{row.freight_rate}</TableCell>
                            <TableCell align="right">₹{row.freight_amt}</TableCell>
                            <TableCell align="right">{row.tds_per}%</TableCell>
                            <TableCell align="right">₹{row.tds_amt}</TableCell>
                            <TableCell align="right">₹{row.shortage_rate}</TableCell>
                            <TableCell align="right" sx={{ color: T.red }}>
                              ₹{row.shortage_amt}
                            </TableCell>
                            <TableCell align="right">₹{row.ad_mines_exp}</TableCell>
                            <TableCell align="right">₹{row.ad_diesel_exp}</TableCell>
                            <TableCell align="right">₹{row.ad_cash_exp}</TableCell>
                            <TableCell align="right">₹{row.total_advance}</TableCell>
                            <TableCell align="right">₹{row.commission}</TableCell>
                            <TableCell align="right">₹{row.net_amount}</TableCell>
                            <TableCell align="right">₹{row.other_exp}</TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontWeight: 700, color: brand }}>₹{row.net_payable}</Typography>
                            </TableCell>
                            <TableCell align="right">₹{row.paid_amount?.toFixed(1)}</TableCell>
                            <TableCell align="right">₹{row.bal_amount?.toFixed(1)}</TableCell>
                            <TableCell>{row.truck_owner}</TableCell>
                            <TableCell>{row.phone_no}</TableCell>
                            <TableCell>{row.pan_no}</TableCell>
                            <TableCell>
                              <Tooltip title={row.remark} arrow>
                                <Typography sx={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.6rem" }}>
                                  {row.remark || "-"}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{formatDate(row.created_at)}</TableCell>
                            <TableCell sx={stickyBodyCell(undefined, rowBgColor, { right: 0 })}>
                              <Stack direction="row" spacing={0.5}>
                                <ActionButton tip="Edit" icon={<EditIcon />} color={brand} onClick={() => handleEditOpen(row)} />
                                <ActionButton tip="Delete" icon={<DeleteIcon />} color={T.red} onClick={(e) => { setDeleteAnchorEl(e.currentTarget); setDeleteId(row.freight_id); }} />
                                <ActionButton tip="Print" icon={<PrintIcon />} color={T.success} onClick={() => handlePrintSingleRow(row)} />
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}

                    {/* Total Row */}
                    {filteredData.length > 0 && (
                      <TableRow sx={{ bgcolor: brandBg, "& td": { fontWeight: "bold", fontSize: "0.7rem", ...noBorderB } }}>
                        <TableCell colSpan={11} align="right">Total:</TableCell>
                        <TableCell align="right">{totals.loading_wt}</TableCell>
                        <TableCell align="right">{totals.received_wt}</TableCell>
                        <TableCell align="right">{totals.shortage_wt}</TableCell>
                        <TableCell align="right">{totals.billing_wt}</TableCell>
                        <TableCell></TableCell>
                        <TableCell align="right">{formatCurrency(totals.freight_amt)}</TableCell>
                        <TableCell></TableCell>
                        <TableCell align="right">{formatCurrency(totals.tds_amt)}</TableCell>
                        <TableCell></TableCell>
                        <TableCell align="right">{formatCurrency(totals.shortage_amt)}</TableCell>
                        <TableCell colSpan={3}></TableCell>
                        <TableCell align="right">{formatCurrency(totals.total_advance)}</TableCell>
                        <TableCell align="right">{formatCurrency(totals.commission)}</TableCell>
                        <TableCell align="right">{formatCurrency(totals.net_amount)}</TableCell>
                        <TableCell align="right">{formatCurrency(totals.other_exp)}</TableCell>
                        <TableCell align="right" sx={{ color: brand, fontWeight: "bold" }}>
                          {formatCurrency(totals.net_payable)}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(totals.paid_amount)}</TableCell>
                        <TableCell align="right">{formatCurrency(totals.bal_amount)}</TableCell>
                        <TableCell colSpan={6}></TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </InfiniteScroll>
          </Box>
        </Paper>
      )}

      {/* ── Edit Dialog ── */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { ...noRadius, ...noBorder, ...noShadow, overflow: "hidden" } }}
      >
        <Box sx={{ bgcolor: brand, px: 3, py: 2 }}>
          <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: "1rem" }}>Edit LR Record</Typography>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editRow.is_remember || false}
                  onChange={(e) => setEditRow((prev) => ({ ...prev, is_remember: e.target.checked }))}
                  sx={{ color: brand, "&.Mui-checked": { color: brand } }}
                />
              }
              label={<Typography sx={{ fontSize: "0.8rem" }}>Remember</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editRow.is_not_rec || false}
                  onChange={(e) => handleEditChange("is_not_rec", e.target.checked)}
                  sx={{ color: brand, "&.Mui-checked": { color: brand } }}
                />
              }
              label={<Typography sx={{ fontSize: "0.8rem" }}>LR Not Received</Typography>}
            />
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Autocomplete
                options={partyOptions}
                getOptionLabel={(opt) => opt?.name || ""}
                value={partyOptions.find((opt) => opt.name === editRow.party_name) || null}
                onChange={(e, newValue) => handleEditChange("party_id", newValue?.party_id || "")}
                renderInput={(params) => <TextField {...params} label="Party Name" size="small" sx={textFieldSx} />}
              />
            </Grid>
            <Grid item xs={6} sm={3}><TextField label="From" fullWidth size="small" value={editRow.from_location || ""} onChange={(e) => handleEditChange("from_location", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="To" fullWidth size="small" value={editRow.to_location || ""} onChange={(e) => handleEditChange("to_location", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="Loading Date" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={editRow.loading_date || ""} onChange={(e) => handleEditChange("loading_date", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="Unloading Date" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={editRow.unloading_date || ""} onChange={(e) => handleEditChange("unloading_date", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="Truck No" fullWidth size="small" value={editRow.truck_no || ""} onChange={(e) => handleEditChange("truck_no", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="Truck Owner" fullWidth size="small" value={editRow.truck_owner || ""} onChange={(e) => handleEditChange("truck_owner", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="Phone No." fullWidth size="small" value={editRow.phone_no || ""} onChange={(e) => handleEditChange("phone_no", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="PAN No" fullWidth size="small" value={editRow.pan_no || ""} onChange={(e) => handleEditChange("pan_no", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="TP No" fullWidth size="small" value={editRow.tp_no || ""} onChange={(e) => handleEditChange("tp_no", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="DO No" fullWidth size="small" value={editRow.do_no || ""} onChange={(e) => handleEditChange("do_no", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="Loading Wt" type="number" fullWidth size="small" value={editRow.loading_wt || ""} onChange={(e) => handleEditChange("loading_wt", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="Receiving Wt" type="number" fullWidth size="small" value={editRow.received_wt || ""} onChange={(e) => handleEditChange("received_wt", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="LR Rate" type="number" fullWidth size="small" value={editRow.freight_rate || ""} onChange={(e) => handleEditChange("freight_rate", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="Shortage Rate" type="number" fullWidth size="small" value={editRow.shortage_rate || ""} onChange={(e) => handleEditChange("shortage_rate", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="TDS %" type="number" fullWidth size="small" value={editRow.tds_per || ""} onChange={(e) => handleEditChange("tds_per", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="Commission" type="number" fullWidth size="small" value={editRow.commission || ""} onChange={(e) => handleEditChange("commission", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="Ad Mines" type="number" fullWidth size="small" value={editRow.ad_mines_exp || ""} onChange={(e) => handleEditChange("ad_mines_exp", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="Ad Diesel" type="number" fullWidth size="small" value={editRow.ad_diesel_exp || ""} onChange={(e) => handleEditChange("ad_diesel_exp", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="Ad Cash" type="number" fullWidth size="small" value={editRow.ad_cash_exp || ""} onChange={(e) => handleEditChange("ad_cash_exp", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField label="Others Exp." type="number" fullWidth size="small" value={editRow.other_exp || ""} onChange={(e) => handleEditChange("other_exp", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={12}><TextField label="Remark" fullWidth multiline rows={2} size="small" value={editRow.remark || ""} onChange={(e) => handleEditChange("remark", e.target.value)} sx={textFieldSx} /></Grid>
            <Grid item xs={6}><TextField label="Receive Amount" type="number" fullWidth size="small" value={editRow.receive_amount || 0} onChange={(e) => handleEditChange("receive_amount", e.target.value)} disabled={!editRow.is_bill} sx={textFieldSx} /></Grid>
            <Grid item xs={6}><TextField label="Difference Amount" fullWidth size="small" value={(editRow.net_amount - editRow.receive_amount).toFixed(2) || ""} disabled sx={disabledFieldSx} /></Grid>
            <Grid item xs={12}><TextField label="Payment Remark" fullWidth size="small" value={editRow.payment_remark || ""} onChange={(e) => handleEditChange("payment_remark", e.target.value)} disabled={!editRow.is_bill} sx={textFieldSx} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, bgcolor: alpha(brand, 0.03) }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ ...noRadius }}>Cancel</Button>
          <Button
            onClick={handleEditSave}
            sx={{
              ...noRadius,
              bgcolor: brand,
              color: "#fff",
              boxShadow: "none",
              "&:hover": { bgcolor: "#4338ca", boxShadow: "none" },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Settle Popover ── */}
      <Popover
        open={Boolean(settleAnchorEl)}
        anchorEl={settleAnchorEl}
        onClose={() => { setSettleAnchorEl(null); setSettleId(null); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { ...noRadius, ...noShadow, bgcolor: alpha(brand, 0.05) } }}
      >
        <Box sx={{ p: 2, maxWidth: 280 }}>
          <Typography sx={{ fontWeight: 500, mb: 1 }}>Settle this LR?</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            Are you sure you want to mark this as completed?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button size="small" onClick={() => { setSettleAnchorEl(null); setSettleId(null); }} sx={{ ...noRadius }}>Cancel</Button>
            <Button size="small" color="success" onClick={() => handleComplete(settleId)} sx={{ ...noRadius }}>Settle</Button>
          </Box>
        </Box>
      </Popover>

      {/* ── Delete Popover ── */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={() => { setDeleteAnchorEl(null); setDeleteId(null); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { ...noRadius, ...noShadow, bgcolor: alpha(brand, 0.05) } }}
      >
        <Box sx={{ p: 2, maxWidth: 280 }}>
          <Typography sx={{ fontWeight: 500, mb: 1 }}>Delete LR Record?</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            This action cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button size="small" onClick={() => { setDeleteAnchorEl(null); setDeleteId(null); }} sx={{ ...noRadius }}>Cancel</Button>
            <Button size="small" color="error" onClick={() => handleDelete(deleteId)} sx={{ ...noRadius }}>Delete</Button>
          </Box>
        </Box>
      </Popover>

      {/* ── Export Dialog ── */}
      <Dialog
        open={excelModalOpen}
        onClose={() => setExcelModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { ...noRadius, ...noBorder, ...noShadow, overflow: "hidden" } }}
      >
        <Box sx={{ bgcolor: brand, px: 3, py: 2 }}>
          <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: "1rem" }}>
            Select Rows for Excel Export
          </Typography>
        </Box>
        <DialogContent sx={{ p: 2 }}>
          <TextField
            fullWidth size="small" placeholder="Search by party name..."
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2, ...textFieldSx }}
          />
          <TableContainer sx={{ maxHeight: "50vh", overflow: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {["", "LR ID", "Truck No", "Party Name", "Loading Date", "Net Payable"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: "bold", fontSize: "0.7rem", bgcolor: alpha(brand, 0.05), ...noBorderB }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData
                  .filter((r) => r.party_name?.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map((row) => (
                    <TableRow key={row.freight_id} hover>
                      <TableCell padding="checkbox" sx={noBorderB}>
                        <Checkbox
                          size="small"
                          checked={selectedRows.includes(row.freight_id)}
                          onChange={(e) =>
                            setSelectedRows((prev) =>
                              e.target.checked
                                ? [...prev, row.freight_id]
                                : prev.filter((id) => id !== row.freight_id)
                            )
                          }
                          sx={{ color: brand, "&.Mui-checked": { color: brand } }}
                        />
                      </TableCell>
                      <TableCell sx={noBorderB}>{row.freight_id}</TableCell>
                      <TableCell sx={noBorderB}>{row.truck_no}</TableCell>
                      <TableCell sx={noBorderB}>{row.party_name}</TableCell>
                      <TableCell sx={noBorderB}>{formatDate(row.loading_date)}</TableCell>
                      <TableCell sx={noBorderB}>₹{row.net_payable}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, bgcolor: alpha(brand, 0.03) }}>
          <Button onClick={() => setExcelModalOpen(false)} sx={{ ...noRadius }}>Cancel</Button>
          <Button
            onClick={handleExportSelectedToExcel}
            sx={{
              ...noRadius,
              bgcolor: brand,
              color: "#fff",
              boxShadow: "none",
              "&:hover": { bgcolor: "#4338ca", boxShadow: "none" },
            }}
          >
            Export Selected ({selectedRows.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ ...noRadius, ...noShadow }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}